/*
 * Johgee first-touch attribution
 *
 * Stores only first-party marketing context: an internal landing path,
 * referrer host/path and recognised campaign parameters. It deliberately
 * never stores full query strings or customer information.
 */
(() => {
  'use strict';

  const STORAGE_KEY = 'johgee_first_touch_v1';
  const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
  const ATTR_PREFIX = 'Attribution · ';
  const campaignKeys = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'gbraid', 'wbraid', 'msclkid', 'fbclid', 'ttclid'
  ];

  const shorten = (value, limit = 220) => String(value || '').trim().slice(0, limit);
  const asPath = (url) => `${url.pathname}${url.searchParams.toString() ? `?${campaignKeys
    .filter((key) => url.searchParams.has(key))
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(url.searchParams.get(key) || '')}`)
    .join('&')}` : ''}`;

  const currentTouch = () => {
    const url = new URL(window.location.href);
    let referrer = '';
    try {
      const referrerUrl = document.referrer ? new URL(document.referrer) : null;
      // A same-site route change is not a new acquisition source.
      if (referrerUrl && referrerUrl.hostname !== url.hostname) {
        referrer = `${referrerUrl.hostname}${referrerUrl.pathname}`;
      }
    } catch (_) {}

    const campaign = {};
    campaignKeys.forEach((key) => {
      const value = shorten(url.searchParams.get(key));
      if (value) campaign[key] = value;
    });

    return {
      recorded_at: new Date().toISOString(),
      storefront: url.hostname,
      landing: shorten(asPath(url)),
      referrer: shorten(referrer),
      campaign
    };
  };

  const canRecord = () => {
    try {
      // Shopify's Customer Privacy API is authoritative when it is present.
      // This prevents marketing attribution storage before a visitor has given
      // the consent required by the storefront's configured privacy settings.
      if (window.Shopify?.customerPrivacy?.userCanBeTracked) {
        return window.Shopify.customerPrivacy.userCanBeTracked();
      }
    } catch (_) {}
    return true;
  };

  const readFirstTouch = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
      if (!value || !value.recorded_at || Date.now() - Date.parse(value.recorded_at) > MAX_AGE_MS) return null;
      return value;
    } catch (_) {
      return null;
    }
  };

  const getFirstTouch = () => {
    const saved = readFirstTouch();
    if (saved) return saved;
    const touch = currentTouch();
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(touch)); } catch (_) {}
    return touch;
  };

  const orderAttributes = (touch) => {
    const attributes = {
      [`${ATTR_PREFIX}First touch`]: touch.recorded_at,
      [`${ATTR_PREFIX}Storefront`]: touch.storefront,
      [`${ATTR_PREFIX}First landing page`]: touch.landing,
      [`${ATTR_PREFIX}First referrer`]: touch.referrer || '(direct / unavailable)'
    };
    Object.entries(touch.campaign || {}).forEach(([key, value]) => {
      attributes[`${ATTR_PREFIX}${key}`] = value;
    });
    return attributes;
  };

  const syncCart = async () => {
    if (!canRecord()) return { skipped: 'privacy' };
    const root = window.Shopify?.routes?.root || '/';
    const touch = getFirstTouch();
    const attribution = orderAttributes(touch);
    const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!cartResponse.ok) throw new Error('Could not read Shopify cart for attribution');
    const cart = await cartResponse.json();
    const existing = cart.attributes || {};
    const changed = Object.entries(attribution).some(([key, value]) => existing[key] !== value);
    if (!changed) return { synced: false, touch };
    const updateResponse = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      // Retain any existing order/cart attributes set elsewhere in the theme.
      body: JSON.stringify({ attributes: { ...existing, ...attribution } })
    });
    if (!updateResponse.ok) throw new Error('Could not save Shopify cart attribution');
    return { synced: true, touch };
  };

  // Start before any cart preload or Buy Now action. Other storefront scripts
  // await this promise before adding an item or sending a customer to checkout.
  window.JohgeeAttribution = {
    ready: Promise.resolve().then(syncCart).catch((error) => ({ error: error.message })),
    sync: () => syncCart()
  };
})();
